import React, { ReactNode, useState } from 'react';
import {
  Box,
  Button,
  ButtonMode,
  ButtonSize,
  DocumentIcon,
  Link,
  Popover
} from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import { getTemplateUrl } from '@epi-pages/Beneficiaries/helpers/getTemplateUrl';
import { FileFormatEnum } from '@epi-models/pages/Beneficiaries';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';

const FileFormatButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  background: none;
  border: none;
  text-decoration: none;
  text-align: left;
  color: ${({ theme }) => theme.primaryFrontColor};
  font-size: ${({ theme }) => theme.sizeMedium};
  font-family: ${({ theme }) => theme.fontFamily};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.primaryBackColorLight10};
    text-decoration: none;
  }

  &:focus,
  &:active {
    text-decoration: none;
  }
`;

interface FileFormatDropdownProps {
  buttonId: string;
  buttonText: string;
  icon: ReactNode;
  formatButtonIdPrefix: string;
  onFormatSelect?: (format: 'xlsx' | 'csv') => void;
}

export const FileFormatDropdown = ({
  buttonId,
  buttonText,
  icon,
  formatButtonIdPrefix,
  onFormatSelect
}: FileFormatDropdownProps) => {
  const { primaryFrontColor } = useTheme();
  const { t } = useTranslation();
  const language = useSelector(getApplicationLanguageId);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const closeDropdown = () => setAnchorEl(null);

  const handleFormatSelect = (format: 'xlsx' | 'csv') => {
    onFormatSelect?.(format);
    closeDropdown();
  };

  return (
    <Box>
      <Button
        id={buttonId}
        mode={ButtonMode.Secondary}
        size={ButtonSize.Medium}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          {buttonText}
        </Box>
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        disableAutoFocus
      >
        <Box display="flex" flexDirection="column">
          <FileFormatButton
            id={`${formatButtonIdPrefix}-xlsx`}
            href={
              onFormatSelect
                ? undefined
                : getTemplateUrl(language, FileFormatEnum.XLSX)
            }
            onClick={() => handleFormatSelect('xlsx')}
          >
            <Box display="flex" gap={1.5} py={1} px={2}>
              <DocumentIcon color={primaryFrontColor} />
              {t('containers.beneficiaries.import_employees.file_format.xlxs')}
            </Box>
          </FileFormatButton>
          <FileFormatButton
            id={`${formatButtonIdPrefix}-csv`}
            href={
              onFormatSelect
                ? undefined
                : getTemplateUrl(language, FileFormatEnum.CSV)
            }
            onClick={() => handleFormatSelect('csv')}
          >
            <Box display="flex" gap={1.5} py={1} px={2}>
              <DocumentIcon color={primaryFrontColor} />
              {t('containers.beneficiaries.import_employees.file_format.csv')}
            </Box>
          </FileFormatButton>
        </Box>
      </Popover>
    </Box>
  );
};
